import React from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { LocalizedLink } from '../LocalizedLink';

export const Footer: React.FC = () => {
  const { t } = useTranslation();
  
  const navItems = [
    {
      name: t('navigation.features'),
      link: "/features",
    },
    {
      name: t('navigation.pricing'),
      link: "/pricing",
    },
    {
      name: t('navigation.contact'),
      link: "/contact",
    },
  ];
  
  const resources = [
    {
      name: t('footer.faq'),
      link: "/faq",
    },
  ];
  
  
  const account = [
    {
      name: t('footer.privacyPolicy'),
      link: "/privacy",
    },
    {
      name: t('footer.termsOfService'),
      link: "/terms",
    },
  ];

  return (
    <div className="border-t border-neutral-100 dark:border-white/[0.1] px-8 py-20 bg-white dark:bg-neutral-950 w-full relative overflow-hidden">
      <div className="max-w-7xl mx-auto text-sm text-neutral-500 flex sm:flex-row flex-col justify-between items-start md:px-8">
        <div>
          <div className="mr-0 md:mr-4 md:flex mb-4">
            <LocalizedLink
              to="/"
              className="font-normal flex space-x-2 items-center text-sm mr-4 text-black dark:text-white px-2 py-1 relative z-20"
            >
              <h1 className="logo-font text-xl ml-2 text-gray-800 dark:text-white hover:text-primary-600/90 transition-all duration-300 transform hover:scale-[1.01] cursor-default select-none font-light">
                {t('landing.headerTitle')}
              </h1>
            </LocalizedLink>
          </div>

          <div className="mt-2 ml-2">
            {t('footer.copyright', { year: new Date().getFullYear(), company: t('landing.headerTitle') })}
          </div>
        </div>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 items-start mt-10 sm:mt-0 md:mt-0">
          <div className="flex justify-center space-y-4 flex-col w-full">
            <p className="transition-colors hover:text-text-neutral-800 text-neutral-600 dark:text-neutral-300 font-bold">
              {t('footer.about')}
            </p>
            <ul className="transition-colors hover:text-text-neutral-800 text-neutral-600 dark:text-neutral-300 list-none space-y-4">
              {navItems.map((item, idx) => (
                <li key={`nav-${idx}`} className="list-none">
                  {item.link.startsWith('/') ? (
                    <LocalizedLink
                      className="transition-colors hover:text-text-neutral-800"
                      to={item.link}
                    >
                      {item.name}
                    </LocalizedLink>
                  ) : (
                    <a
                      className="transition-colors hover:text-text-neutral-800"
                      href={item.link}
                    >
                      {item.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex justify-center space-y-4 flex-col">
            <p className="transition-colors hover:text-text-neutral-800 text-neutral-600 dark:text-neutral-300 font-bold">
              {t('footer.resources')}
            </p>
            <ul className="transition-colors hover:text-text-neutral-800 text-neutral-600 dark:text-neutral-300 list-none space-y-4">
              {resources.map((item, idx) => (
                <li key={`resource-${idx}`} className="list-none">
                  <LocalizedLink
                    className="transition-colors hover:text-text-neutral-800"
                    to={item.link}
                  >
                    {item.name}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="flex justify-center space-y-4 flex-col">
            <p className="transition-colors hover:text-text-neutral-800 text-neutral-600 dark:text-neutral-300 font-bold">
              {t('footer.account')}
            </p>
            <ul className="transition-colors hover:text-text-neutral-800 text-neutral-600 dark:text-neutral-300 list-none space-y-4">
              {account.map((item, idx) => (
                <li key={`account-${idx}`} className="list-none">
                  <LocalizedLink
                    className="transition-colors hover:text-text-neutral-800"
                    to={item.link}
                  >
                    {item.name}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex justify-center space-y-4 flex-col">
            <p className="transition-colors hover:text-text-neutral-800 text-neutral-600 dark:text-neutral-300 font-bold">
              {t('footer.support')}
            </p>
            <div className="space-y-4">
              <a
                href="https://invoicehappens.featurebase.app"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border border-blue-200/50 dark:border-blue-800/30 rounded-lg transition-all duration-200 hover:from-blue-100 hover:to-indigo-100 dark:hover:from-blue-900/30 dark:hover:to-indigo-900/30 hover:border-blue-300/60 dark:hover:border-blue-700/50 hover:shadow-md hover:shadow-blue-100/50 dark:hover:shadow-blue-900/20 transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <svg 
                  className="w-4 h-4 text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors duration-200" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300 group-hover:text-blue-800 dark:group-hover:text-blue-200 transition-colors duration-200">
                  {t('footer.feedback')}
                </span>
                <svg 
                  className="w-3 h-3 text-blue-500 dark:text-blue-400 group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-all duration-200 group-hover:translate-x-0.5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
      <p className="text-center mt-20 text-5xl md:text-8xl lg:text-[7rem] xl:text-[10rem] font-bold bg-clip-text text-transparent bg-gradient-to-b from-neutral-50 dark:from-neutral-950 to-neutral-200 dark:to-neutral-800 inset-x-0">
        {t('landing.headerTitle')}
      </p>
    </div>
  );
}; 